<template>
    <form
        method=""
        action=""
        class="form-group"
        @submit.prevent="onSubmit"
        @keydown="getCurrentForm().errors.clear($event.target.name)"
        ref="form"
        autocomplete="off"
    >
        <div id="loader" v-if="isLoading">
            <div class="spinner"></div>
        </div>

        <div class="progress-bar-container">
            <ProgressBar
                :active-step="currentStepNumber"
                :total-steps="totalSteps"
            ></ProgressBar>
        </div>

        <component
            :is="{...currentStepComponent}"
            shallowRef="currentStepRef"
            :course="course"
            :step="currentStep"
            :fields="currentStepFields"
            :form-data="formData[currentStepNumber - 1] || sessionOnLoad"
        ></component>

        <div class="form__buttons form-group py-3">
            <button
                type="button"
                class="btn btn-secondary me-2"
                data-cy="btn-back"
                @click.prevent="goBack"
            >
                Go Back
            </button>
            <button class="btn btn-primary" data-cy="btn-next" data-testid="btn-next">
                {{ currentStepNumber === totalSteps ? "Submit" : "Next" }}
            </button>
        </div>
        <p
            class="form-error"
            v-if="getCurrentForm() && getCurrentForm().errors.get('application-status')"
            role="alert"
            aria-live="assertive"
        >
            {{ getCurrentForm().errors.get("application-status") }}
        </p>
    </form>
</template>

<script>
import ProgressBar from "./ProgressBar.vue";
import StepEmployment from "./Application/StepEmployment";
import StepEducation from "./Application/StepEducation";
import StepHealth from "./Application/StepHealth";
import StepYourCircumstances from "./Application/StepYourCircumstances";
import StepHowDidYouHearAboutUs from "./Application/StepHowDidYouHearAboutUs";
import StepEmergencyContactDetails from "./Application/StepEmergencyContactDetails";
import StepExploreEnterprise from "./Application/StepExploreEnterprise";

export default {
    components: {
        ProgressBar,
    },
    props: {
        steps: Array,
        course: String,
        interactionid: String,
        // A snapshot of the users session when the page initially loads, used to prepopulate forms
        sessionOnLoad: Object,
    },
    computed: {
        totalSteps() {
            return this.steps.length;
        },
        currentStep() {
            return this.steps[this.currentStepNumber - 1];
        },
        currentStepComponent() {
            return this.stepComponents[this.currentStepNumber - 1];
        },
        currentStepFields() {
            let originalFields = this.currentStep.fields;

            for (const fieldName in originalFields) {
                let field = originalFields[fieldName];

                if (field.validation && Array.isArray(field.validation)) {
                    field.required = field.validation.includes("required");
                    if (
                        field.validation.filter((s) =>
                            s.includes("required_if")
                        ).length > 0
                    ) {
                        let validatedFieldValues = field.validation
                            .find((s) => s.includes("required_if"))
                            .replace("required_if:", "")
                            .split(",");
                        field.conditionalRequiredFieldName =
                            validatedFieldValues[0];
                        field.conditionalRequiredFieldValues = validatedFieldValues.slice(
                            1
                        );
                    }
                }
            }
            return originalFields;
        },
    },
    data: function () {
        return {
            // An real-time copy of the form data for each step
            formData: Array(this.totalSteps),
            isLoading: false,
            currentStepNumber: 1,
            stepComponents: [],
        };
    },
    created() {
        let stepIdToComponentMappings = {
            education: StepEducation,
            employment: StepEmployment,
            health: StepHealth,
            yourCircumstances: StepYourCircumstances,
            howDidYouHearAboutUs: StepHowDidYouHearAboutUs,
            emergencyContactDetails: StepEmergencyContactDetails,
            exploreEnterprise: StepExploreEnterprise,
        };

        this.stepComponents = this.steps.map((step) => {
            return stepIdToComponentMappings[step.id];
        });

        window.onpopstate = (event) => {
            this.loadStepFromUrl();
        };

        this.loadStepFromUrl();
    },
    methods: {
        getCurrentForm() {
            return this.$refs.currentStepRef && this.$refs.currentStepRef.form;
        },
        loadStepFromUrl() {
            const url = new URL(window.location);
            let parsedStepNumber = Number.parseInt(
                url.searchParams.get("step")
            );

            this.currentStepNumber = parsedStepNumber || 1;
        },
        addStepToUrl() {
            const url = new URL(window.location);
            url.searchParams.set("step", this.currentStepNumber);
            window.history.pushState({}, "", url);
        },
        onSubmit() {
            this.isLoading = true;

            this.formData[
                this.currentStepNumber - 1
            ] = this.getCurrentForm().data();

            this.getCurrentForm()
                .post(`/courses/${this.course}/apply/${this.currentStep.id}/${this.interactionid}`)
                .then((response) => {
                    this.goForward();
                })
                .catch(this.scrollToFirstError)
                .finally(() => (this.isLoading = false));
        },
        goBack() {
            if (this.currentStepNumber <= 1) {
                window.location.href = `/${this.course}-about-you`;
            } else {
                this.currentStepNumber--;
                this.addStepToUrl();
                this.scrollToBeginning();
            }
        },
        goForward() {
            if (this.currentStepNumber < this.totalSteps) {
                this.currentStepNumber++;
                this.addStepToUrl();
                this.scrollToBeginning();
            } else {
                window.location.href = "/form-completion";
            }
        },
        scrollToBeginning() {
            const form = this.$refs.form;

            if (form) {
                const { top } = form.getBoundingClientRect();
                const offsetY = Math.max(0, window.pageYOffset + top - 30);
                window.scrollTo(0, offsetY);
            }
        },
        scrollToFirstError() {
            const form = this.getCurrentForm();

            // If we don't have a form or errors, don't go any further.
            if(!form || !form.errors.any()) {
                return;
            }

            const error = document.getElementsByClassName('form-error')[0];

            if(error) {
                // Find the parent element (question) of the error class.
                const parent = error.parentNode;
                const { top } = parent.getBoundingClientRect();

                // Scroll to just above the parent element so we've got clear context.
                const offsetY = Math.max(0, window.pageYOffset + top - 30);
                window.scrollTo({ top: offsetY, behavior: 'smooth' });
            }
        }
    },
};
</script>
